/**
 * Client-side OpenAPI spec and Scalar API portal generation for model inference endpoints.
 *
 * Ported from scripts/admin/models/generate_scalar_inference_portal.py
 */

const SCALAR_CDN_URL = 'https://cdn.jsdelivr.net/npm/@scalar/api-reference@1.25.0';

const JSON_SCHEMA_TYPES = new Set([
  'object', 'array', 'string', 'integer', 'number', 'boolean', 'null',
]);

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function slugify(value) {
  const slug = String(value ?? '').trim().toLowerCase().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'value';
}

/**
 * Returns true if the object looks like a JSON Schema rather than a simple key-value schema.
 * Uses structural checks to avoid false positives on domain field names like "items" or "type".
 */
function looksLikeJsonSchema(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  // 'properties' as a non-null object is a strong JSON Schema indicator
  if (obj.properties !== null && typeof obj.properties === 'object') return true;
  // Combinator keywords are unambiguously JSON Schema
  if (obj.allOf || obj.oneOf || obj.anyOf || obj.$ref || obj.$defs) return true;
  // 'type' alone is ambiguous; only trust it if the value is a valid JSON Schema type
  if (typeof obj.type === 'string' && JSON_SCHEMA_TYPES.has(obj.type)) return true;
  return false;
}

/**
 * Normalize a schema to JSON Schema format.
 * If the schema is already JSON Schema (has type/properties/etc), returns it as-is.
 * If it's a simple key-value format ({"age": "int", "income": "float"}), converts to JSON Schema.
 *
 * Note: The simple-to-JSON-Schema conversion is new functionality (not from the Python script)
 * to handle the platform's internal schema format used by older models.
 */
export function normalizeToJsonSchema(schema) {
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
    return { type: 'object', properties: {}, required: [] };
  }

  if (looksLikeJsonSchema(schema)) {
    return schema;
  }

  // Simple key-value format: convert to JSON Schema
  const properties = {};
  const required = [];

  for (const [key, typeStr] of Object.entries(schema)) {
    required.push(key);
    switch (String(typeStr).toLowerCase()) {
      case 'str':
      case 'string':
        properties[key] = { type: 'string' };
        break;
      case 'int':
      case 'integer':
        properties[key] = { type: 'integer' };
        break;
      case 'float':
      case 'number':
        properties[key] = { type: 'number' };
        break;
      case 'bool':
      case 'boolean':
        properties[key] = { type: 'boolean' };
        break;
      case 'array':
        properties[key] = { type: 'array', items: {} };
        break;
      case 'object':
        properties[key] = { type: 'object' };
        break;
      default:
        properties[key] = {};
        break;
    }
  }

  return { type: 'object', properties, required };
}

/**
 * Infer a representative JSON example from a JSON Schema.
 * Port of infer_example_from_schema from the Python script.
 */
export function inferExampleFromSchema(schema, depth = 0) {
  if (depth > 16 || !schema || typeof schema !== 'object') {
    return null;
  }

  if ('example' in schema) {
    return structuredClone(schema.example);
  }

  if (Array.isArray(schema.examples) && schema.examples.length > 0) {
    return structuredClone(schema.examples[0]);
  }

  if ('default' in schema) {
    return structuredClone(schema.default);
  }

  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return structuredClone(schema.enum[0]);
  }

  let schemaType = schema.type;
  if (Array.isArray(schemaType)) {
    const nonNull = schemaType.filter(t => t !== 'null');
    schemaType = nonNull.length > 0 ? nonNull[0] : 'null';
  }

  if (schemaType === 'object' || (schemaType == null && typeof schema.properties === 'object')) {
    const properties = schema.properties || {};
    if (typeof properties !== 'object') return {};
    const example = {};
    for (const [key, propSchema] of Object.entries(properties)) {
      example[key] = inferExampleFromSchema(propSchema, depth + 1);
    }
    return example;
  }

  if (schemaType === 'array') {
    const itemSchema = schema.items || {};
    const itemExample = inferExampleFromSchema(itemSchema, depth + 1);
    return itemExample == null ? [] : [itemExample];
  }

  if (schemaType === 'string') {
    const fmt = schema.format;
    if (fmt === 'date-time') return '2026-01-01T00:00:00Z';
    if (fmt === 'date') return '2026-01-01';
    if (fmt === 'uuid') return '00000000-0000-0000-0000-000000000000';
    return 'sample string';
  }

  if (schemaType === 'integer') return 42;
  if (schemaType === 'number') return 3.14;
  if (schemaType === 'boolean') return true;
  if (schemaType === 'null') return null;

  // Handle allOf — merge all subschemas' examples into one object
  if (Array.isArray(schema.allOf) && schema.allOf.length > 0) {
    const merged = {};
    for (const sub of schema.allOf) {
      const ex = inferExampleFromSchema(sub, depth + 1);
      if (ex && typeof ex === 'object' && !Array.isArray(ex)) {
        Object.assign(merged, ex);
      }
    }
    return Object.keys(merged).length > 0 ? merged : inferExampleFromSchema(schema.allOf[0], depth + 1);
  }

  // Handle anyOf / oneOf — pick first non-null variant
  for (const key of ['anyOf', 'oneOf']) {
    const variants = schema[key];
    if (Array.isArray(variants) && variants.length > 0) {
      const nonNull = variants.filter(v => !(typeof v === 'object' && v && v.type === 'null'));
      return inferExampleFromSchema((nonNull.length > 0 ? nonNull : variants)[0], depth + 1);
    }
  }

  return null;
}

/**
 * Build standard inference response definitions for the OpenAPI spec.
 * Port of build_inference_responses from the Python script.
 */
function buildInferenceResponses(responseSchema, responseExample) {
  const errorSchema = {
    type: 'object',
    properties: {
      error: { type: 'string', description: 'Human-readable error message.' },
    },
    required: ['error'],
  };

  const coldStartSchema = {
    type: 'object',
    properties: {
      error: { type: 'string', enum: ['endpoint_cold_start'] },
      message: { type: 'string' },
      endpoint_name: { type: 'string' },
      retry_after_seconds: { type: 'integer' },
    },
    required: ['error', 'message', 'endpoint_name', 'retry_after_seconds'],
  };

  const successContent = { schema: responseSchema };
  if (responseExample != null) {
    successContent.example = responseExample;
  }

  return {
    '200': {
      description: 'Inference succeeded.',
      content: { 'application/json': successContent },
    },
    '400': {
      description: 'Invalid request payload.',
      content: {
        'application/json': {
          schema: errorSchema,
          example: { error: 'Batch request size exceeds maximum limit of 8 items' },
        },
      },
    },
    '500': {
      description: 'Server error while processing inference request.',
      content: {
        'application/json': {
          schema: errorSchema,
          example: { error: 'Model inference failed: <details>' },
        },
      },
    },
    '503': {
      description: 'Model endpoint is temporarily unavailable. Retry later.',
      headers: {
        'Retry-After': {
          description: 'Number of seconds to wait before retrying.',
          schema: { type: 'integer' },
        },
      },
      content: {
        'application/json': {
          schema: coldStartSchema,
          example: {
            error: 'endpoint_cold_start',
            message: 'Model endpoint is warming up, please retry in 2-3 minutes',
            endpoint_name: 'your-model-endpoint',
            retry_after_seconds: 120,
          },
        },
      },
    },
  };
}

/**
 * Build a complete OpenAPI 3.1.0 spec for a single model's inference endpoint.
 * Simplified single-model version of build_openapi_spec from the Python script.
 */
export function buildOpenApiSpec({
  modelName,
  modelId,
  inputSchema,
  outputSchema,
  sampleRequest,
  apiBaseUrl = 'https://api.plexe.ai',
}) {
  const normalizedInput = normalizeToJsonSchema(inputSchema);
  const normalizedOutput = normalizeToJsonSchema(outputSchema);

  // Use explicit sample_request if available, otherwise infer from schema
  const requestExample =
    sampleRequest && typeof sampleRequest === 'object' && Object.keys(sampleRequest).length > 0
      ? sampleRequest
      : inferExampleFromSchema(normalizedInput);
  const responseExample = inferExampleFromSchema(normalizedOutput);

  const endpointPath = `/models/${encodeURIComponent(modelName)}/${encodeURIComponent(modelId)}/infer`;
  const operationId = `infer_${slugify(modelName)}_${slugify(modelId)}`;

  const requestContent = { schema: normalizedInput };
  if (requestExample != null) {
    requestContent.example = requestExample;
  }

  return {
    openapi: '3.1.0',
    info: {
      title: `${modelName} Inference API`,
      version: '1.0.0',
      description: 'Inference endpoint for a deployed Plexe model.',
    },
    servers: [{ url: apiBaseUrl.replace(/\/+$/, '') }],
    paths: {
      [endpointPath]: {
        post: {
          operationId,
          summary: `Infer with model '${modelName}'`,
          security: [{ ApiKeyAuth: [] }],
          requestBody: {
            required: true,
            content: { 'application/json': requestContent },
          },
          responses: buildInferenceResponses(normalizedOutput, responseExample),
        },
      },
    },
    components: {
      securitySchemes: {
        ApiKeyAuth: {
          type: 'apiKey',
          in: 'header',
          name: 'x-api-key',
          description: 'Plexe API key.',
        },
      },
    },
  };
}

/**
 * Build a standalone HTML page embedding a Scalar API reference UI.
 * Port of build_scalar_html from the Python script.
 */
export function buildScalarHtml(spec, title) {
  const specJson = JSON.stringify(spec);
  const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(specJson)}`;

  const scalarConfig = {
    spec: { url: dataUri },
    authentication: {
      preferredSecurityScheme: 'ApiKeyAuth',
    },
    persistAuth: true,
  };
  const configJson = JSON.stringify(scalarConfig);

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title)}</title>
  <style>
    html, body {
      margin: 0;
      padding: 0;
      min-height: 100%;
      width: 100%;
      background: #ffffff;
    }
  </style>
</head>
<body>
  <script
    id="api-reference"
    data-configuration="${escapeHtml(configJson)}">
  </script>
  <script src="${SCALAR_CDN_URL}"></script>
</body>
</html>
`;
}

/**
 * Trigger a browser file download from in-memory content.
 * Matches the existing pattern in api.js (createObjectURL + appendChild/removeChild).
 */
export function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
